import { useState } from 'react';
import type { Invoice, Subscription } from '@salonomy/types';
import { Button, Card, Pill, Spinner } from '@salonomy/ui';
import { ApiClientError } from '../../lib/api.js';
import {
  useDunning,
  useInvoices,
  usePlans,
  useReactivateSubscription,
  useSubscription,
} from '../../data/billing.js';
import { money, pluralize } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';
import { CancelDialog } from './CancelDialog.js';
import { ChairLine } from './ChairLine.js';
import { ChangePlanModal } from './ChangePlanModal.js';
import { DunningBanner } from './DunningBanner.js';
import {
  billingDate,
  daysUntil,
  invoiceLabel,
  invoiceTone,
  subscriptionLabel,
  subscriptionTone,
} from './format.js';
import { InvoiceBreakdown } from './InvoiceBreakdown.js';
import { PaymentMethodModal } from './PaymentMethodModal.js';
import './billing.css';

/**
 * WS10 — the console SaaS-billing surface (brief 10 §3/§4). This is the PROOF of the FOUNDATION promise:
 * "No contracts, ever. Monthly rolling, in-app one-click cancel, itemised invoices." It is owner-only
 * (the API gates every billing route to the owner; we mirror that here so a non-owner never gets a bare
 * 403), and EVERY amount it shows is a server figure — the subscription's items, each invoice's totals,
 * the per-invoice /breakdown. There is no money, VAT or proration math anywhere in this feature.
 *
 * DESIGN NOTE (brief 10 §design): this trust surface is NOT yet designed and WILL be screenshotted in
 * marketing. It is built from the existing Settings card/token idiom as an honest placeholder; a design
 * round (the landing page's published-margins editorial treatment) is wanted before launch.
 */
export function BillingPanel({ isOwner }: { isOwner: boolean }) {
  // Non-owners see an honest note, not a 403 — mirrors the API's owner-only billing gate. The reads
  // below stay disabled for them so we never fire a request that can only fail.
  const subQuery = useSubscription(isOwner);
  const dunningQuery = useDunning(isOwner);

  const [cardModal, setCardModal] = useState(false);

  if (!isOwner) {
    return (
      <Card>
        <div className="section-title">Billing</div>
        <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
          No contracts, ever. Monthly rolling, itemised invoices, one-click cancel. Only the salon
          owner can view and manage the subscription and invoices.
        </div>
      </Card>
    );
  }

  // A 404 from the subscription read means "no subscription yet" — that's a normal pre-subscribe state,
  // not an error. Anything else is a real failure.
  const noSubscription =
    subQuery.isError && subQuery.error instanceof ApiClientError && subQuery.error.status === 404;

  return (
    <Card>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div>
          <div className="section-title">Billing</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
            No contracts, ever. Monthly rolling, itemised invoices, one-click cancel.
          </div>
        </div>
        <Pill tone="soft" className="mono">
          Design pending
        </Pill>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
        {dunningQuery.data ? (
          <DunningBanner dunning={dunningQuery.data} onUpdateCard={() => setCardModal(true)} />
        ) : null}

        {subQuery.isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Spinner size={16} />
            <span className="muted" style={{ fontSize: 13 }}>
              Loading your subscription…
            </span>
          </div>
        ) : noSubscription || !subQuery.data ? (
          <NoSubscription error={subQuery.isError && !noSubscription} />
        ) : (
          <SubscriptionBody subscription={subQuery.data} onAddCard={() => setCardModal(true)} />
        )}
      </div>

      <PaymentMethodModal
        open={cardModal}
        onClose={() => setCardModal(false)}
        hasExisting={Boolean(subQuery.data)}
      />
    </Card>
  );
}

/** No subscription yet (or an unexpected read error). Subscribe is a small, honest CTA. */
function NoSubscription({ error }: { error: boolean }) {
  if (error) {
    return (
      <div className="muted" style={{ fontSize: 13 }}>
        Couldn’t load your billing right now. Every figure here is read live — please try again in a
        moment.
      </div>
    );
  }
  return (
    <div
      style={{
        border: '1px dashed var(--slm-border)',
        borderRadius: 12,
        padding: '16px 18px',
      }}
    >
      <div style={{ fontWeight: 600 }}>You’re not subscribed yet</div>
      <div className="muted" style={{ fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>
        Start a no-commitment monthly plan when you’re ready — a free trial first, a card added with
        no charge, and one-click cancel whenever you like. Subscribing is wired through the pricing
        page at launch.
      </div>
    </div>
  );
}

/** The lifecycle header + per-chair line + invoices + action row for an existing subscription. */
function SubscriptionBody({
  subscription,
  onAddCard,
}: {
  subscription: Subscription;
  onAddCard: () => void;
}) {
  const toast = useToast();
  const { data: plans } = usePlans();
  const reactivate = useReactivateSubscription();

  const [planModal, setPlanModal] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);

  const baseItem = subscription.items.find((i) => i.kind === 'base');
  const perChairItem = subscription.items.find((i) => i.kind === 'per_chair');

  const isTrial = subscription.status === 'trialing';
  const scheduledCancel = subscription.cancelAtPeriodEnd;
  const trialDays = isTrial ? daysUntil(subscription.trialEnd) : 0;

  async function doReactivate() {
    try {
      await reactivate.mutateAsync();
      toast.success('Welcome back — your subscription will continue.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not reactivate.');
    }
  }

  return (
    <>
      {/* ── Lifecycle header ── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>{subscription.planCode}</span>
          <Pill tone={subscriptionTone(subscription.status)}>
            {subscriptionLabel(subscription.status)}
          </Pill>
          {scheduledCancel ? <Pill tone="danger">Cancels at period end</Pill> : null}
        </div>

        {isTrial ? (
          <div className="muted" style={{ fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
            You’re on a free trial — <strong>{pluralize(trialDays, 'day')}</strong> left
            {subscription.trialEnd ? ` (until ${billingDate(subscription.trialEnd)})` : ''}. Add a
            card now and you won’t be charged until the trial ends.{' '}
            <button
              type="button"
              className="slm-btn slm-btn--ghost slm-btn--sm"
              style={{ paddingLeft: 0 }}
              onClick={onAddCard}
            >
              Add a card
            </button>
          </div>
        ) : subscription.currentPeriodEnd ? (
          <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>
            {scheduledCancel
              ? `Active until ${billingDate(subscription.currentPeriodEnd)}, then cancels.`
              : `Renews on ${billingDate(subscription.currentPeriodEnd)}.`}
          </div>
        ) : null}

        {scheduledCancel ? (
          <div className="muted" style={{ fontSize: 12.5, marginTop: 8, lineHeight: 1.5 }}>
            Changed your mind? Reactivating clears the scheduled cancel — just as easily.{' '}
            <Button size="sm" variant="soft" onClick={doReactivate} disabled={reactivate.isPending}>
              {reactivate.isPending ? 'Reactivating…' : 'Reactivate'}
            </Button>
          </div>
        ) : null}
      </div>

      {/* ── Plan lines: base + per-chair (with the staff behind the count) ── */}
      {baseItem && baseItem.unitPricePence > 0 ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 12,
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 13.5 }}>Base plan</div>
          <span className="billing-line__amount">{money(baseItem.unitPricePence)}</span>
        </div>
      ) : null}

      <ChairLine perChairItem={perChairItem} />

      {/* ── Latest invoice breakdown + history ── */}
      <InvoiceSection />

      {/* ── Action row ── */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          paddingTop: 12,
          borderTop: '1px solid var(--slm-border)',
        }}
      >
        <Button size="sm" variant="soft" onClick={onAddCard}>
          {isTrial ? 'Add a card' : 'Replace card'}
        </Button>
        <Button
          size="sm"
          variant="soft"
          onClick={() => setPlanModal(true)}
          disabled={!plans || plans.length === 0}
        >
          Change plan
        </Button>
        {!scheduledCancel ? (
          <Button size="sm" variant="ghost" onClick={() => setCancelDialog(true)}>
            Cancel subscription
          </Button>
        ) : null}
      </div>

      {plans ? (
        <ChangePlanModal
          open={planModal}
          onClose={() => setPlanModal(false)}
          plans={plans}
          currentPlanCode={subscription.planCode}
        />
      ) : null}

      <CancelDialog
        open={cancelDialog}
        onClose={() => setCancelDialog(false)}
        subscription={subscription}
      />
    </>
  );
}

/** The latest invoice's itemised breakdown, plus the history list (each row opens its own breakdown). */
function InvoiceSection() {
  const { data: page, isLoading, isError } = useInvoices();
  const invoices = page?.data ?? [];
  const [selected, setSelected] = useState<string | null>(null);

  // Default the open breakdown to the newest invoice (the API returns newest first).
  const latest = invoices[0] ?? null;
  const openId = selected ?? latest?.id ?? null;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Spinner size={16} />
        <span className="muted" style={{ fontSize: 13 }}>
          Loading invoices…
        </span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="muted" style={{ fontSize: 13 }}>
        Couldn’t load your invoices. They’re read live from your billing data — try again shortly.
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div
        style={{
          paddingTop: 12,
          borderTop: '1px solid var(--slm-border)',
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 13.5 }}>Invoices</div>
        <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
          No invoices yet. Your first one is issued at the end of your trial or first cycle — and
          it’ll explain every line.
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: 12, borderTop: '1px solid var(--slm-border)' }}>
      <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 4 }}>
        Why this invoice is this amount
      </div>

      {/* The selected (default: newest) invoice's full itemised breakdown — every figure server-derived. */}
      {openId ? <InvoiceBreakdown invoiceId={openId} /> : null}

      {/* History — each row opens its own breakdown above. */}
      <div style={{ marginTop: 16 }}>
        <div className="muted" style={{ fontSize: 11.5, marginBottom: 4 }}>
          Invoice history
        </div>
        <div className="settings-list">
          {invoices.map((inv) => (
            <InvoiceRow
              key={inv.id}
              invoice={inv}
              open={inv.id === openId}
              onOpen={() => setSelected(inv.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function InvoiceRow({
  invoice,
  open,
  onOpen,
}: {
  invoice: Invoice;
  open: boolean;
  onOpen: () => void;
}) {
  return (
    <div className="settings-row">
      <div>
        <div style={{ fontWeight: 600, fontSize: 13 }}>
          {invoice.number ?? 'Draft invoice'}
          {open ? (
            <Pill tone="cobalt" className="mono">
              Showing
            </Pill>
          ) : null}
        </div>
        <div className="muted" style={{ fontSize: 12 }}>
          {invoice.periodStart && invoice.periodEnd
            ? `${billingDate(invoice.periodStart)} – ${billingDate(invoice.periodEnd)}`
            : billingDate(invoice.issuedAt ?? invoice.createdAt)}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Pill tone={invoiceTone(invoice.status)}>{invoiceLabel(invoice.status)}</Pill>
        <span className="mono" style={{ fontSize: 13 }}>
          {money(invoice.totalPence)}
        </span>
        {invoice.invoicePdfUrl ? (
          <a
            href={invoice.invoicePdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="slm-btn slm-btn--ghost slm-btn--sm"
          >
            PDF
          </a>
        ) : null}
        {!open ? (
          <Button size="sm" variant="ghost" onClick={onOpen}>
            Why this amount
          </Button>
        ) : null}
      </div>
    </div>
  );
}
